import { motion } from "framer-motion";
import {
  Monitor,
  Stethoscope,
  Heart,
  CheckCircle,
  ArrowRight,
  Cpu,
  Layers,
  BarChart3,
} from "lucide-react";
import Section from "../ui/Section";
import Card from "../ui/Card";
import Button from "../ui/Button";

const modules = [
  {
    icon: Monitor,
    title: "Radyolojik Görüntüleme",
    subtitle: "BT & MR Modülü",
    description:
      "Bilgisayarlı tomografi ve manyetik rezonans görüntüleme sırasında AI destekli gerçek zamanlı konuşma tanıma. ~200ms işleme süresi ile kesintisiz rapor oluşturma.",
    features: [
      "BT/MR sırasında gerçek zamanlı konuşma tanıma",
      "%95+ konuşma tanıma doğruluğu",
      "Anatomik terminoloji SNOMED CT eşleştirme",
      "Önceki tetkiklerle karşılaştırmalı analiz",
      "Optimize edilmiş hafif model",
    ],
    gradient: "from-primary-500 to-primary-700",
    metric: { value: "%95+", label: "Konuşma tanıma doğruluğu" },
    techBadges: ["AI", "SNOMED CT", "Gerçek Zamanlı"],
  },
  {
    icon: Stethoscope,
    title: "Ultrasonografi ve Doppler",
    subtitle: "İç Hastalıklar Modülü",
    description:
      "Ultrason sırasında çok katmanlı doğal dil işleme ile anında yapılandırılmış rapor. Organ, patoloji, ölçüm, lateralite ve lokalizasyon otomatik tanıma.",
    features: [
      "5 farklı tıbbi varlık türü otomatik tanıma",
      "Yüksek doğruluklu ilişki çıkarımı",
      "%94 kapsama oranı",
      "AI ile ~200 etiketli örnek yeterli",
      "Rapor başına maliyet: $0.03",
    ],
    gradient: "from-emerald-500 to-teal-600",
    metric: { value: "%94", label: "Kapsama oranı" },
    techBadges: ["NLP", "SNOMED CT", "SPARQL"],
  },
  {
    icon: Heart,
    title: "Kardiyoloji (EKO)",
    subtitle: "Ekokardiyografi Modülü",
    description:
      "3 seviyeli yanıt mimarisi ile kardiyak yapı değerlendirmesi. Akıllı önbellekleme ile <1ms, paralel arama ile <80ms yanıt süresi.",
    features: [
      "Akıllı önbellekleme: %75 anında yanıt",
      "Hızlı benzerlik araması: 42ms",
      "1,200 eşzamanlı kullanıcı desteği",
      "EF ve ölçüm entegrasyonu",
      "WebSocket ile gerçek zamanlı iletişim",
    ],
    gradient: "from-sky-500 to-blue-600",
    metric: { value: "<2.1s", label: "Toplam yanıt süresi" },
    techBadges: ["Önbellek", "Arama", "WebSocket"],
  },
];

const commonFeatures = [
  { icon: Cpu, label: "PACS/HIS", desc: "Sorunsuz sistem entegrasyonu" },
  { icon: Layers, label: "KVKK", desc: "AES-256 + TLS 1.3 güvenlik" },
  { icon: BarChart3, label: "3 Dil", desc: "TR, EN, Latin terminoloji" },
  { icon: Monitor, label: "Esnek", desc: "Bulut veya lokal kurulum" },
];

export default function Modules() {
  return (
    <Section id="modules" background="white" padding="xl">
      <div className="text-center mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-display-sm md:text-display-md font-display font-bold text-gray-900 dark:text-white mb-5">
            Uzmanlaşmış{" "}
            <span className="text-gradient">Modüller</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Her uzmanlık alanı için özel olarak tasarlanmış, akademik düzeyde doğrulanmış modüller
          </p>
        </motion.div>
      </div>

      <div className="space-y-8">
        {modules.map((module, index) => {
          const Icon = module.icon;
          const isEven = index % 2 === 0;

          return (
            <motion.div
              key={module.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.15 }}
            >
              <Card variant="glass" padding="none" className="overflow-hidden">
                <div
                  className={`grid grid-cols-1 lg:grid-cols-5 gap-0 ${
                    isEven ? "" : "lg:grid-flow-col-dense"
                  }`}
                >
                  {/* Content - 3 columns */}
                  <div className={`p-8 lg:col-span-3 ${isEven ? "" : "lg:col-start-3"}`}>
                    <div className="flex items-center space-x-4 mb-5">
                      <div
                        className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${module.gradient} flex items-center justify-center shadow-lg`}
                      >
                        <Icon size={28} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-display font-bold text-gray-900 dark:text-white">
                          {module.title}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                          {module.subtitle}
                        </p>
                      </div>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                      {module.description}
                    </p>

                    {/* Tech badges */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {module.techBadges.map((badge) => (
                        <span
                          key={badge}
                          className="px-2.5 py-1 text-xs font-mono font-medium rounded-lg bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400 border border-gray-200/60 dark:border-white/10"
                        >
                          {badge}
                        </span>
                      ))}
                    </div>

                    <div className="space-y-2.5 mb-6">
                      {module.features.map((feature, featureIndex) => (
                        <motion.div
                          key={feature}
                          initial={{ opacity: 0, x: -15 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0.4,
                            delay: index * 0.15 + featureIndex * 0.06,
                          }}
                          className="flex items-center space-x-3"
                        >
                          <CheckCircle
                            size={16}
                            className="text-emerald-500 flex-shrink-0"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {feature}
                          </span>
                        </motion.div>
                      ))}
                    </div>

                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        document
                          .getElementById("contact")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }
                    >
                      Detaylı Bilgi Al
                      <ArrowRight size={16} />
                    </Button>
                  </div>

                  {/* Metric Visual - 2 columns */}
                  <div
                    className={`lg:col-span-2 ${
                      isEven ? "" : "lg:col-start-1"
                    } flex items-center justify-center p-8`}
                  >
                    <div
                      className={`w-full max-w-xs aspect-square rounded-3xl bg-gradient-to-br ${module.gradient} flex flex-col items-center justify-center relative overflow-hidden`}
                    >
                      <div className="absolute inset-0 bg-black/10" />
                      <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full" />
                      <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-white/5 rounded-full" />
                      <div className="relative z-10 text-center text-white">
                        <Icon size={48} className="mx-auto mb-4 opacity-80" />
                        <div className="text-4xl font-bold mb-1">{module.metric.value}</div>
                        <p className="text-sm opacity-80 px-4">{module.metric.label}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Common Features */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-12"
      >
        <Card variant="gradient" padding="lg">
          <h3 className="text-xl font-display font-bold text-center text-gray-900 dark:text-white mb-8">
            Ortak Altyapı
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {commonFeatures.map((feat) => {
              const FIcon = feat.icon;
              return (
                <div key={feat.label} className="space-y-2">
                  <FIcon
                    size={24}
                    className="mx-auto text-primary-600 dark:text-primary-400"
                  />
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {feat.label}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {feat.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </Card>
      </motion.div>
    </Section>
  );
}
